"use client"
import { useState, useEffect } from "react"
import type React from "react"

import { Button } from "../../components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog"
import { getTempToken } from "../../api/authService"
import { Upload, Plus } from "lucide-react"

interface ImagePickerModalProps {
  onImageSelected: (imageUrl: string, imageId: number) => void
  onClose: () => void
}

interface MediaItem {
  id: number
  title: string
  source_url: string
  media_details?: {
    width: number
    height: number
  }
}

export function ImagePickerModal({ onImageSelected, onClose }: ImagePickerModalProps) {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [selectedImage, setSelectedImage] = useState<MediaItem | null>(null)
  const [showUpload, setShowUpload] = useState(false)

  useEffect(() => {
    fetchMediaLibrary()
  }, [])

  const fetchMediaLibrary = async () => {
    try {
      setLoading(true)
      const token = await getTempToken()
      const res = await fetch("https://tsoministries.com/wp-json/wp/v2/media?per_page=100", {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (res.ok) {
        const data = await res.json()
        setMediaItems(data)
      }
    } catch (error) {
      console.error("[v0] Error fetching media library:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (!files || files.length === 0) return

    setUploading(true)
    const formData = new FormData()
    formData.append("file", files[0])

    try {
      const token = await getTempToken()
      const res = await fetch("https://tsoministries.com/wp-json/wp/v2/media", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })

      if (res.ok) {
        const newMedia = await res.json()
        setMediaItems([newMedia, ...mediaItems])
        setSelectedImage(newMedia)
        // Automatically select the uploaded image
        handleSelectImage(newMedia)
      }
    } catch (error) {
      console.error("[v0] Error uploading image:", error)
      alert("Error uploading image")
    } finally {
      setUploading(false)
      setShowUpload(false)
    }
  }

  const handleSelectImage = (media: MediaItem) => {
    setSelectedImage(media)
    onImageSelected(media.source_url, media.id)
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Select or Upload Image</DialogTitle>
          <DialogDescription>Choose an image from your media library or upload a new one</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload Section */}
          {!showUpload && (
            <Button onClick={() => setShowUpload(true)} className="w-full gap-2">
              <Upload className="h-4 w-4" />
              Upload New Image
            </Button>
          )}

          {showUpload && (
            <div className="border-2 border-dashed rounded-lg p-8 text-center bg-gray-50">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <div className="space-y-2">
                  <Plus className="h-8 w-8 mx-auto text-gray-400" />
                  <p className="text-sm text-gray-600">
                    {uploading ? "Uploading..." : "Drag and drop or click to upload"}
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Media Library Grid */}
          {loading ? (
            <div className="text-center py-8">Loading media library...</div>
          ) : mediaItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No images found</div>
          ) : (
            <div className="grid grid-cols-3 gap-4 max-h-96 overflow-y-auto">
              {mediaItems.map((media) => (
                <button
                  key={media.id}
                  onClick={() => handleSelectImage(media)}
                  className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage?.id === media.id ? "border-blue-500 ring-2 ring-blue-500" : "border-gray-200"
                  }`}
                >
                  <img
                    src={media.source_url || "/placeholder.svg"}
                    alt={media.title}
                    className="w-full h-32 object-cover hover:opacity-80"
                  />
                  {selectedImage?.id === media.id && (
                    <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                      <div className="bg-white rounded-full p-2">
                        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4">
            <Button onClick={onClose} variant="outline" className="flex-1 bg-transparent">
              Cancel
            </Button>
            <Button onClick={onClose} disabled={!selectedImage} className="flex-1">
              Insert Image
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
